import { Stack } from '@chakra-ui/react'
import type { InsuranceDto, InsuranceParamsLeasingDto } from 'BrokerTsLib/src/dtos'
import { PolicyStatusType } from 'BrokerTsLib/src/enums'

import { CancelStornoRequest } from '@/components/CancelStornoRequest'
import { ChangeNewButton } from '@/components/ChangeNewButton'
import { MarkAsCancelledButton } from '@/components/MarkAsCancelledButton'
import { StornoPs } from '@/components/StornoPs'
import { useInsuranceSagaEvents, useInsuranceSagas } from '@/context/InsuranceSagaContext'

/**
 * VWFS storno akce na detailu smlouvy. U smlouvy čekající na storno (PendingCancellation) nabídne místo
 * „Storno PS" zrušení čekajícího požadavku; id sagy dodává InsuranceSagaContext. Události sag (SagaWorkflowHub)
 * obnovují GetInsurance, takže stav i tlačítka se přepnou bez ručního reloadu.
 *
 * Osoby a vozidlo (pro „Změna novou") se čtou z entity Insurance (`insurance`), ne z InsuranceParams.
 */
export const VwfsStornoActions = ({
  insurance,
  insuranceParams,
  leasingContractNumber,
  callback,
}: {
  insurance: InsuranceDto
  insuranceParams: InsuranceParamsLeasingDto
  leasingContractNumber?: string | null
  callback: () => void
}) => {
  const insuranceId = insurance.insuranceId!
  const { policyStatus, expectedEndDate, requestedEndDate } = insurance

  const { activeSagas, refetchSagas } = useInsuranceSagas()

  useInsuranceSagaEvents(() => {
    callback()
    refetchSagas()
  })

  // Akce mění sagy pojištění (Storno PS založí čekající sagu, zrušení ji ukončí) — spolu s GetInsurance je
  // třeba obnovit i seznam sag, jinak se tlačítko nepřepne (hub event nedorazí, dokud nejsme přihlášeni
  // k odběru kořene nové sagy).
  const onActionDone = () => {
    callback()
    refetchSagas()
  }

  // Čekající storno = CANCELLATION saga před prvním pokusem (PENDING_START, příp. QUEUED za FIFO předchůdcem).
  const pendingCancellationSaga = activeSagas.find(
    s => s.eventType === 'CANCELLATION' && (s.stateName === 'PENDING_START' || s.stateName === 'QUEUED'),
  )

  return (
    <Stack mb={4}>
      {policyStatus === PolicyStatusType.PendingCancellation && pendingCancellationSaga ? (
        <CancelStornoRequest
          sagaId={pendingCancellationSaga.sagaId}
          callback={onActionDone}
        />
      ) : (
        <StornoPs
          insuranceId={insuranceId}
          callback={onActionDone}
        />
      )}

      <MarkAsCancelledButton
        insuranceId={insuranceId}
        expectedEndDate={expectedEndDate}
        requestedEndDate={requestedEndDate}
        callback={onActionDone}
      />

      <ChangeNewButton
        insurance={insurance}
        insuranceParams={insuranceParams}
        leasingContractNumber={leasingContractNumber}
        callback={onActionDone}
      />
    </Stack>
  )
}
